import type { RawDriverLocation } from "./appsyncRealtime";
import type { DriverActivity, DriverPosition, DriverProfile } from "./types";

/**
 * 配送追蹤 session 的純函式（不 import axios，測試才不會拖進 Sentry／攔截器）。
 */

/** 通行證到期前多久續發。續發時會重連一次，留 2 分鐘給網路慢的時候。 */
export const REFRESH_LEAD_MS = 120_000;
/**
 * 多久確認一次「這張單還在配送中嗎」。
 *
 * AppSync 只會推座標，不會告訴我們訂單結束了；而訂單詳情頁在觀看期間不會重查訂單狀態。
 * 不主動問的話，配送完成後畫面會繼續即時顯示配送員的位置，直到通行證到期續發
 * （最長約 8 分鐘）才發現——那時他已經在前往下一位客戶的路上，等於把別人的配送
 * 行程顯示給這位客戶看。
 *
 * 60 秒是權衡：夠快讓完成後很快收掉，又不至於讓一張配送中的單每分鐘打好幾次 API。
 * 注意這只是縮短暴露時間，真正的保證在後端——訂單結束後授權器就不該再放行這個訂閱。
 */
export const AVAILABILITY_CHECK_MS = 60_000;

/** 斷線重連間隔：第一次 3 秒、之後每 10 秒（與後台 FleetMap 的 10 秒一致）。 */
export const RECONNECT_DELAYS_MS = [3_000, 10_000] as const;
/**
 * 續發最少要等這麼久。
 * 手機時鐘偏快時（expiresAt 是伺服器時間、now 是手機時間）算出來的延遲可能是 0 甚至負數，
 * 沒有下限的話每次 start() 成功就會立刻再 start() 一次，變成無限迴圈。
 */
export const MIN_REFRESH_DELAY_MS = 30_000;
/**
 * 續發最多等這麼久：通行證 600 秒效期扣掉 120 秒提前量，是時鐘準確時本來就會等到的上限。
 * 手機時鐘偏慢時算出來的延遲可能遠大於這個值，加上限避免真的等到通行證已經過期才續發。
 */
export const MAX_REFRESH_DELAY_MS = 480_000;

/** POST /app-api/platform-orders/:id/tracking-session 的 data（與 app-api TrackingSession 相同）。 */
export type TrackingSession =
  | { available: false }
  | {
      available: true;
      token: string;
      expiresAt: number;
      courierId: string;
      realtime: { httpHost: string; wssUrl: string };
      driver: DriverProfile | null;
      position: Partial<RawDriverLocation> | null;
      eta: { from: string; to: string } | null;
      stopsAhead: number | null;
    };

export const refreshDelayMs = (expiresAt: number, now: number): number =>
  Math.min(MAX_REFRESH_DELAY_MS, Math.max(MIN_REFRESH_DELAY_MS, expiresAt - REFRESH_LEAD_MS - now));

export const reconnectDelayMs = (attempt: number): number =>
  RECONNECT_DELAYS_MS[attempt] ?? RECONNECT_DELAYS_MS[RECONNECT_DELAYS_MS.length - 1];

const ACTIVITIES: DriverActivity[] = ["in_vehicle", "on_foot", "still", "unknown"];

const finite = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;

export function toDriverPosition(raw: Partial<RawDriverLocation> | null | undefined): DriverPosition | null {
  if (!raw) return null;
  const lat = finite(raw.lat);
  const lng = finite(raw.lng);
  const rawTs = finite(raw.ts);
  if (lat === null || lng === null || rawTs === null) return null;
  const activity =
    raw.activity == null
      ? null
      : ACTIVITIES.includes(raw.activity as DriverActivity)
        ? (raw.activity as DriverActivity)
        : "unknown";
  return {
    lat,
    lng,
    bearing: finite(raw.bearing),
    // 裝置偶爾回報秒；統一成毫秒（與後台一致）
    ts: rawTs < 1e12 ? rawTs * 1000 : rawTs,
    activity,
    isMoving: typeof raw.isMoving === "boolean" ? raw.isMoving : null,
  };
}
